export default `<h1 id="ai-readiness-assessment-service">AI Readiness Assessment
Service</h1>
<p><strong>Helping organisations build the foundations for successful,
sustainable AI adoption.</strong></p>
<p>Our AI Readiness Assessment is a structured diagnostic that evaluates
your current capabilities across data, technology, people and governance
and delivers a clear, prioritised roadmap to take you from initial
proof-of-concepts to scalable, production-grade AI solutions.</p>
<hr />
<h2 id="our-objectives">Our Objectives</h2>
<ul>
<li><strong>Benchmark</strong> – Understand where you stand today in
terms of data quality, infrastructure, skills and processes.</li>
<li><strong>Identify and then surface gaps &amp; risks</strong> –
Whether in data cleanliness, toolchain maturity, team skills or
ethical/governance controls.</li>
<li><strong>Prioritise quick wins</strong> – Pinpoint high-impact areas
where targeted investments or pilot projects can deliver measurable ROI
within weeks.</li>
<li><strong>Build out a roadmap</strong> – Lay out a step-by-step plan
that sequences capability building, pilot sprints, governance setup and
full roll-outs.</li>
</ul>
<hr />
<h2 id="assessment-framework-methodology">Assessment Framework &amp;
Methodology</h2>
<p>We structure the assessment around four pillars, using interviews,
workshops and technical audits to score each dimension on a 5-point
maturity scale:</p>
<table>
<colgroup>
<col style="width: 26%" />
<col style="width: 73%" />
</colgroup>
<thead>
<tr class="header">
<th>PILLAR</th>
<th>FOCUS AREA</th>
</tr>
</thead>
<tbody>
<tr class="odd">
<td><strong>Data Foundations</strong></td>
<td>• Data quality &amp; lineage <br> • Metadata &amp; cataloguing <br>
• Integration &amp; pipelines</td>
</tr>
<tr class="even">
<td><strong>Technology Stack</strong></td>
<td>• Compute &amp; storage architecture <br> • MLops pipelines &amp;
automation <br> • Toolchain fit</td>
</tr>
<tr class="odd">
<td><strong>People &amp; Skills</strong></td>
<td>• Team structure &amp; roles <br> • Skill gaps (data engineering,
ML, devops) <br> • Learning culture</td>
</tr>
<tr class="even">
<td><strong>Governance &amp; Ethics</strong></td>
<td>• Model explainability &amp; audit trails <br> • Data privacy &amp;
security policies <br> • Bias &amp; fairness controls</td>
</tr>
</tbody>
</table>
<hr />
<h2 id="key-activities">Key Activities</h2>
<ul>
<li><strong>Kick-off Workshop (2 hrs):</strong> Align on scope, success
criteria, key stakeholders.</li>
<li><strong>Stakeholder Interviews (4–6 interviews):</strong> CTO, Head
of Data/AI, Business Leads, Compliance.</li>
<li><strong>Technical Audit (2 days):</strong> Review existing data
architectures, sample pipelines, model artifacts, and security
controls.</li>
<li><strong>Skills &amp; Culture Survey:</strong> Quick pulse on team
capabilities and appetite for experimentation.</li>
<li><strong>Governance Review:</strong> Map existing policies against
best-practice frameworks (e.g., GDPR, model audit standards).</li>
</ul>
<hr />
<h2 id="deliverables">4. Deliverables</h2>
<h3 id="ai-readiness-report">AI Readiness Report:</h3>
<ul>
<li>Maturity scores for each pillar.</li>
<li>Detailed gap analysis with evidence (e.g., data-quality exception
rates, CI/CD lead-times - (Continuous Integration and Continuous
Delivery/Deployment)).</li>
</ul>
<h3 id="quick-win-opportunities">Quick-Win Opportunities:</h3>
<ul>
<li>2–3 targeted pilot ideas (e.g., “30-day POC for an identified
pilot)</li>
<li>Estimated effort, resources and expected ROI.</li>
</ul>
<h3 id="strategic-roadmap">Strategic Roadmap:</h3>
<ul>
<li>A Gantt chart of capability-building activities (data-engineering
sprints, governance rollout, MLOps platform build).</li>
<li>Milestones, dependencies and success metrics.</li>
</ul>
<h3 id="executive-presentation">Executive Presentation:</h3>
<ul>
<li>Summary findings for leadership, recommended next steps, and
budgetary guidance.</li>
</ul>
<hr />
<h2 id="timeline-investment">5. Timeline &amp; Investment</h2>
<table>
<colgroup>
<col style="width: 23%" />
<col style="width: 12%" />
<col style="width: 64%" />
</colgroup>
<thead>
<tr class="header">
<th>Phase</th>
<th>Duration</th>
<th>Client Time Commit</th>
</tr>
</thead>
<tbody>
<tr class="odd">
<td>Kick-off &amp; Planning</td>
<td>1 week</td>
<td>2-hr workshop + stakeholder availability</td>
</tr>
<tr class="even">
<td>Discovery &amp; Audit</td>
<td>2 weeks</td>
<td>4–6 stakeholder interviews; code/access reviews</td>
</tr>
<tr class="odd">
<td>Analysis &amp; Reporting</td>
<td>1 week</td>
<td>Feedback session on draft findings</td>
</tr>
<tr class="even">
<td>Roadmap Finalisation</td>
<td>1 week</td>
<td>Executive review &amp; sign-off</td>
</tr>
</tbody>
</table>
<p><strong>Total Duration: 5 weeks</strong><br />
<strong>Typical Engagement Fee: £25k–£35k</strong> (depending on
organisational complexity)</p>
<p><em>Time spans are subject to specific project deliverables but typical engagements follow this pattern.</em></p>
<hr />
<h2 id="why-choose-our-assessment">Why Choose Our Assessment?</h2>
<p>By the end of this AI Readiness Assessment, you’ll not only
understand your current maturity, but also have a crystal-clear plan to
move forward. Turning data into decisions and pilots into production,
all within a culture that embraces curiosity and exploration.</p>
`;
